import type { DayHours, Discount, GeneralSettings } from "../../api/types";
import { t } from "../../i18n";
import { formatAmount, parseAmount } from "../../lib/money";

/**
 * Helpers for Beheer › Algemeen, as in the prototype: hours grouped as on the receipt, the IBAN in
 * blocks of 4 with its check digits, and the lines of the receipt's header and footer.
 */

/** Days in a row with the same hours together (prototype groupHours): "Maandag t/m Zondag: 17:00 tot 23:00". */
export function groupHours(hours: readonly DayHours[]): [string, string][] {
  const { days } = t.hours;
  const same = (a: DayHours | undefined, b: DayHours | undefined) =>
    JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const groups: [string, string][] = [];
  let first = 0;
  while (first < days.length) {
    let last = first;
    while (last + 1 < days.length && same(hours[last + 1], hours[first])) last++;
    const label =
      first === last
        ? days[first]!
        : last === first + 1
          ? t.hours.and(days[first]!, days[last]!)
          : t.hours.through(days[first]!, days[last]!);
    const day = hours[first] ?? null;
    groups.push([label, day ? t.hours.range(day[0], day[1]) : t.hours.closed]);
    first = last + 1;
  }
  return groups;
}

/**
 * The details of the shop itself (Furkan, 03/10/2026): what a receipt, a mail and the bank-QR say about
 * who sells. Only Tafo sets them, in Tafo-beheer; the till shows them and the server does not read them
 * from a till.
 */
export const SHOP_DETAILS = [
  "receiptName",
  "displayName",
  "company",
  "street",
  "postcode",
  "city",
  "vatNumber",
  "iban",
] as const satisfies readonly (keyof GeneralSettings)[];
export type ShopDetails = Pick<GeneralSettings, (typeof SHOP_DETAILS)[number]>;
/** What the shop sets itself: how to reach it, its hours, its receipt text and how its apps behave. */
export type OwnGeneral = Omit<GeneralSettings, keyof ShopDetails>;

/** The shop's own part of the settings: what Beheer › Algemeen saves. */
export function ownGeneral(general: GeneralSettings): OwnGeneral {
  const details: readonly string[] = SHOP_DETAILS;
  return Object.fromEntries(Object.entries(general).filter(([key]) => !details.includes(key))) as OwnGeneral;
}

/** The shop's details out of its settings: what Tafo-beheer shows and saves. */
export function shopDetails(general: ShopDetails): ShopDetails {
  return Object.fromEntries(SHOP_DETAILS.map((key) => [key, general[key]])) as ShopDetails;
}

const ibanClean = (value: string) => value.replace(/\s/g, "").toUpperCase();

/** "BE22 0019 0559 8847" */
export const ibanFormat = (value: string) => ibanClean(value).replace(/(.{4})(?=.)/g, "$1 ");

/** The check digits of an IBAN (modulo 97); a Belgian one has 16 characters. */
export function ibanValid(value: string): boolean {
  const iban = ibanClean(value);
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{8,30}$/.test(iban)) return false;
  if (iban.startsWith("BE") && iban.length !== 16) return false;
  const digits = (iban.slice(4) + iban.slice(0, 4)).replace(/[A-Z]/g, (letter) => String(letter.charCodeAt(0) - 55));
  let rest = 0;
  for (const digit of digits) rest = (rest * 10 + Number(digit)) % 97;
  return rest === 1;
}

/** While typing: blocks of 4, the cursor stays after the same character. */
export function ibanTyping(value: string, cursor: number): { value: string; cursor: number } {
  const before = value.slice(0, cursor).replace(/\s/g, "").length;
  const formatted = ibanFormat(value);
  let position = 0;
  let counted = 0;
  while (position < formatted.length && counted < before) {
    if (formatted[position] !== " ") counted++;
    position++;
  }
  return { value: formatted, cursor: position };
}

/** "BE0755.336.723", also typed with spaces or without dots; new firms start with 1 ("BE1012.345.678"). */
export const vatNumberOk = (value: string) => /^BE ?[01]?\d{3}\.?\d{3}\.?\d{3}$/i.test(value.replace(/\s/g, ""));

export const emailOk = (value: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);

/** One line per line of the text field, empty lines left out, at most 4 (prototype). */
export const textLines = (text: string) =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 4);

/** "Kapelstraat 73, 9140 Temse" */
export const shopAddress = (general: GeneralSettings) => `${general.street}, ${general.postcode} ${general.city}`;

/** Minutes typed for a preparation time: 0 to 240, otherwise null. */
export function readMinutes(text: string): number | null {
  if (!/^\d{1,3}$/.test(text.trim())) return null;
  const minutes = Number(text.trim());
  return minutes <= 240 ? minutes : null;
}

/** The discounts every till offered until the shops could choose their own (Furkan, 13/10/2026). */
export const DEFAULT_DISCOUNTS: readonly Discount[] = [
  ...[50, 100, 200, 400, 500].map((amountCents) => ({ amountCents })),
  ...[5, 10, 15, 20, 25, 30, 50].map((percent) => ({ percent })),
];
/** As many "Korting" buttons as fit the checkout. */
export const MAX_DISCOUNTS = 12;

/** The "Korting" buttons of the checkout: what the shop chose in Beheer › Algemeen, else the list every till had. */
export function checkoutDiscounts(general: GeneralSettings | null): readonly Discount[] {
  return general?.discounts ?? DEFAULT_DISCOUNTS;
}

/** On its button: "−4", "−0,50", "−10%". */
export function discountLabel(discount: Discount): string {
  if ("percent" in discount) return `−${String(discount.percent)}%`;
  const amount = formatAmount(discount.amountCents);
  return `−${amount.endsWith(",00") ? amount.slice(0, -3) : amount}`;
}

/** The amounts first, small to large, then the percentages: the order of the buttons. */
export function sortDiscounts(discounts: readonly Discount[]): Discount[] {
  const rank = (discount: Discount) => ("percent" in discount ? 1_000_000 + discount.percent : discount.amountCents);
  return [...discounts].sort((a, b) => rank(a) - rank(b));
}

/**
 * A discount as the owner types it in Beheer › Algemeen: "4" or "0,50" is an amount in euro, "10%" a
 * percentage (1 to 100). Null when it is neither.
 */
export function readDiscount(text: string): Discount | null {
  const typed = text
    .trim()
    .replace(/^[−-]\s*/, "")
    .replace(/^€\s*/, "");
  const percent = /^(\d{1,3})\s*%$/.exec(typed);
  if (percent) {
    const value = Number(percent[1]);
    return value >= 1 && value <= 100 ? { percent: value } : null;
  }
  const amountCents = parseAmount(typed);
  return amountCents !== null && amountCents >= 1 && amountCents <= 100_000 ? { amountCents } : null;
}
